import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { refreshTokenV2 } from "../services/api";
import { baseURL } from "../services/axiosConfig";
import WhiteDropDownArrow from "../Asserts/images/white_dropdown.svg"
import { useDispatch, useSelector } from "react-redux";
import { fetchHomeFixtures } from "./fixturesAndResult.slice";
import useWebSocket from "../websoket/websocket";


const isCheckStatusLive = status => !['FT', 'AET', 'PEN', 'NS'].includes(status);

const MobileFixtureCard = ({ fixture }) => (
  <div className="flex items-center min-w-[270px] max-w-[290px] bg-white rounded-xl px-4 py-3 mx-1">
    <div className="flex flex-col items-center flex-shrink-0 w-16">
      <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center overflow-hidden border border-[#E0E6F0]">
        <img
          src={fixture?.hometeam_logo}
          alt={fixture?.hometeam}
          className="h-10 w-10 object-contain"
        />
      </div>
      <div className="text-xs text-[#444] font-semibold uppercase tracking-wider mt-1">
        {fixture?.short?.find((s) => s.team_type === "home")?.code || ""}
      </div>
    </div>
    <div className="flex flex-col items-center flex-shrink-0 mx-6 min-w-[60px]">
      {fixture?.status === "FT" || isCheckStatusLive(fixture?.status) ? (
        <>
          <span className="text-lg font-bold text-[#222] whitespace-nowrap">
            {fixture.hometeam_goal} - {fixture.awayteam_goal}
          </span>
          <div className="mt-1">{statusBadge(fixture.status)}</div>
        </>
      ) : (
        <span className="text-base font-semibold text-[#222] whitespace-nowrap">
          {formatMatchTime(fixture.start)}
        </span>
      )}
    </div>
    <div className="flex flex-col items-center flex-shrink-0 w-16">
      <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center overflow-hidden border border-[#E0E6F0]">
        <img
          src={fixture.awayteam_logo}
          alt={fixture.awayteam}
          className="h-10 w-10 object-contain"
        />
      </div>
      <div className="text-xs text-[#444] font-semibold uppercase tracking-wider mt-1">
        {fixture.short?.find((s) => s?.team_type === "away")?.code || ""}
      </div>
    </div>
  </div>
);


const ChevronDown = ({ className = "" }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    className={className}
  >
    <path
      d="M6 8L10 12L14 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


function formatMatchTime(dateString) {
  const date = new Date(dateString);
  let hours = date.getHours();
  let mins = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${hours}:${mins.toString().padStart(2, "0")} ${ampm}`;
}



function formatDate(dateString) {
  const date = new Date(dateString);
  const formatted = date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
  return formatted.replace(/^(\w+) /, '$1, ');
}



const statusBadge = (status) => {
  if (status === "FT") {
    return (
      <span className="text-xs font-bold text-[#0DAD7D] bg-[#E9F8F3] px-3 py-1 rounded-full">
        FT
      </span>
    );
  }
  if (isCheckStatusLive(status)) {
    return (
      <span className="text-xs font-bold text-white bg-[#0DAD7D] px-3 py-1 rounded-full flex items-center gap-1">
        <span className="w-2 h-2 bg-white/70 rounded-full animate-pulse"></span>
        LIVE
      </span>
    );
  }
  return null;
};


function Dropdown({ options, value, onChange, className = "", placeholder = "Select", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const ref = useRef(null);
  const dropdownRef = useRef(null);


  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleScroll(e) {
      if (open && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);


  useEffect(() => {
    if (open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 224; // max-h-56 in pixels (14rem = 224px)


      // If there's not enough space below and more space above, show dropdown upward
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }
    }
  }, [open]);


  return (
    <div ref={ref} className={`relative w-full sm:w-auto ${className}`}>
      <button
        type="button"
        className={`
          flex items-center justify-between px-2 sm:px-4 h-9 sm:h-11 rounded-lg bg-transparent border border-white/30 
          text-xs sm:text-base font-medium text-white min-w-[100px] sm:min-w-[185px] w-full sm:w-auto 
          focus:outline-none transition hover:bg-white/10
          ${open ? "ring-2 ring-white/20 bg-white/10" : ""}
          ${disabled ? "cursor-not-allowed opacity-50" : ""}
        `}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className="truncate sm:hidden">
          {options.find((o) => o.value === value)?.truncatedLabel ||
            options.find((o) => o.value === value)?.label ||
            placeholder}
        </span>
        <span className="truncate hidden sm:block">
          {options.find((o) => o.value === value)?.fullLabel ||
            options.find((o) => o.value === value)?.label ||
            placeholder}
        </span>
        <img src={WhiteDropDownArrow} alt="Dropdown Arrow" className={`ml-2 w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && options.length > 0 && !disabled && (
        <ul
          ref={dropdownRef}
          className={`
            absolute z-[9999] bg-white border border-[#E0E6F0] rounded-lg shadow-lg max-h-56 overflow-auto
            ${dropdownDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"}
            w-full
          `}
        >
          {options.map((opt) => (
            <li
              key={opt.value}
              onClick={() => {
                if (opt.value === "no-leagues" || opt.value === "no-weeks")
                  return;
                onChange(opt.value);
                setOpen(false);
              }}
              className={`px-2 sm:px-4 py-1 sm:py-2 cursor-pointer hover:bg-[#F5F7FA] text-[#222] text-xs sm:text-base ${opt.value === value ? "bg-[#F5F7FA]" : ""
                } ${opt.value === "no-leagues" || opt.value === "no-weeks"
                  ? "text-gray-400 cursor-not-allowed hover:bg-transparent"
                  : ""
                }`}
            >
              {opt.fullLabel || opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


export default function FixturesAndResultsHome({ setSelectedSubNav }) {
  const dispatch = useDispatch();
  const socketRef = useWebSocket("/ws/livefixture/");
  const token = localStorage.getItem("access_token");
  const hasValidToken = token && token !== "null" && token !== "undefined";

  const [fixtures, setFixtures] = useState([]);
  const [matchWeekList, setMatchWeekList] = useState([]);
  const [leagueList, setLeaguesList] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");

  const fetchFixturesRedux = useSelector((state) => state.fixturesAndResults.home);

  useEffect(() => {
    if (fetchFixturesRedux.length > 0) {
      setFixtures(fetchFixturesRedux);

    }
  }, [fetchFixturesRedux]);

  const fetchMatchWeeks = async (leagueId) => {
    try {
      const headers = {};
      if (hasValidToken) {
        headers.Authorization = `Bearer ${JSON.parse(token)}`;
      }
      const url = `${baseURL}/pot/seasonmatchweeks?season=${leagueId}`;
      const response = await axios.get(url, { headers });
      setMatchWeekList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching match weeks:", error);
      setMatchWeekList([]);
    }
  };


  const fetchLeagues = async () => {
    try {
      const headers = {};
      if (hasValidToken) {
        headers.Authorization = `Bearer ${JSON.parse(token)}`;
      }
      const response = await axios.get(`${baseURL}/pot/seasons`, { headers });
      setLeaguesList(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching leagues:", error);
    }
  };


  useEffect(() => {
    fetchLeagues();
  }, [token]);


  useEffect(() => {
    if (selectedLeague && selectedLeague !== "") {
      fetchMatchWeeks(selectedLeague);
      setSelectedWeek("");
    } else {
      setMatchWeekList([]);
      setSelectedWeek("");
    }
  }, [selectedLeague]);


  useEffect(() => {
    const fetchFixtures = async () => {
      try {
        const queryParams = new URLSearchParams();


        if (selectedLeague && selectedLeague !== "") {
          queryParams.append("league", selectedLeague);


          const selectedLeagueObj = leagueList.find(
            (league) => league.id === selectedLeague
          );
          if (selectedLeagueObj && selectedLeagueObj.season_year) {
            queryParams.append("season", selectedLeagueObj.season_year);
          }
        }


        if (selectedWeek && selectedWeek !== "") {
          queryParams.append("matchweek", selectedWeek);
        }


        queryParams.append("group_by_date", true);


        const queryString = queryParams.toString();
        const url = `${baseURL}/pot/fixtures${queryString ? `?${queryString}` : ""
          }`;

        const headers = {};
        if (hasValidToken) {
          headers.Authorization = `Bearer ${JSON.parse(token)}`;
        }

        const response = await axios.get(url, { headers });
        dispatch(fetchHomeFixtures(response?.data?.data || response?.data || []));
      } catch (error) {
        console.error("Error fetching fixtures data:", error);
        if (error.response && error.response.status === 401 && hasValidToken) {
          refreshTokenV2(dispatch);
        }
      }
    };

    fetchFixtures();
  }, [selectedLeague, selectedWeek, token, leagueList]);


  const getLeagueOptions = () => {
    if (leagueList.length === 0) {
      return [
        { value: "no-leagues", label: "No leagues available" },
      ];
    }
    const dynamicOptions = leagueList?.map((league) => {
      const originalLabel = league?.leaguename || league.label;
      return {
        value: league?.id || league.value,
        label: originalLabel,
        fullLabel: originalLabel,
        truncatedLabel:
          originalLabel && originalLabel.length > 10
            ? `${originalLabel.substring(0, 10)}...`
            : originalLabel,
      };
    });
    return dynamicOptions;
  };


  const getMatchWeekOptions = () => {
    if (selectedLeague === "") {
      return [];
    }

    if (matchWeekList?.length === 0) {
      return [
        { value: "no-weeks", label: "No match weeks available" },
      ];
    }

    const dynamicOptions = matchWeekList?.map((week) => {
       let num = week?.matchweek.split("-")[1];
      return {
        value: week?.id || week?.value,
        label: `Match Week - ${num}` || `Match Week ${week.number || week.week_number || week.id}`
      }
    });
    return dynamicOptions;
  };

  const sendTestMessage = () => {
    if (WebSocket.OPEN) {
      console.log("WebSocket is open");
    } else {
      console.log("WebSocket is not open");
    }
  };

  useEffect(() => {
    sendTestMessage()
  }, [])

  return (
    <section className="p-0 bg-white shadow-inner rounded-2xl w-full sm:w-[95%] mx-auto mt-8 mb-8 border border-[#e8e8e8]">
      <div className="mobile-header-bg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 rounded-t-2xl px-3 sm:px-6 py-3 transition-all">
        <h4 className="text-white text-xl sm:text-2xl font-semibold font-archivo m-0 text-left w-full sm:w-auto">
          Fixtures & Results
        </h4>
        <div className="flex flex-row gap-3 w-full sm:w-auto">
          <Dropdown
            options={getLeagueOptions()}
            value={selectedLeague}
            onChange={setSelectedLeague}
            placeholder="Select League"
            className="sm:mr-3 flex-1 sm:flex-none"
          />
          <Dropdown
            options={getMatchWeekOptions()}
            value={selectedWeek}
            onChange={selectedLeague !== "" ? setSelectedWeek : () => { }}
            placeholder={selectedLeague === "" ? "Select Match Week" : "Select Match Week"}
            disabled={selectedLeague === ""}
            className={`flex-1 sm:flex-none ${selectedLeague === ""
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
          />
        </div>
      </div>
      <div className="flex w-full justify-end px-3 sm:px-6 mt-2">
        <button
          className="text-blue-600 bg-[#0081F8] text-xs sm:text-sm text-white px-3 sm:px-4 py-2 rounded-full font-medium"
          onClick={() => {
            setSelectedSubNav("Fixtures & Results");
          }}
        >
          View All
        </button>
      </div>
      <div className="bg-white rounded-b-2xl px-0 py-6 sm:px-10 sm:py-10">
        {fixtures && fixtures.length > 0 ? (
          fixtures.map((day) => (
            <div className="mb-10 sm:mb-14" key={day.date}>
              <div className="text-base sm:text-[12px] md:text-[16px] font-semibold mb-4 sm:mb-8 text-[#111] px-4 sm:px-0">
                {formatDate(day.date)}
              </div>
              <div className="flex overflow-x-auto scrollbar-hide w-full gap-4 px-2 sm:hidden">
                {day.fixtures.map((fixture, i) => (
                  <React.Fragment key={fixture.id}>
                    <MobileFixtureCard fixture={fixture} />
                    {i < day.fixtures.length - 1 && (
                      <div className="flex items-center justify-center">
                        <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#E0E6F0] to-transparent"></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="hidden sm:flex overflow-x-auto scrollbar-hide w-full pb-2 gap-2 sm:gap-3">
                {day.fixtures.map((fixture, index) => {
                  return <React.Fragment key={fixture.id}>
                    <div
                      className="
                        flex flex-row items-center justify-between mx-4 min-w-[280px] max-w-[280px] flex-shrink-0
                        relative bg-white rounded-lg p-4 gap-4 h-24
                      "
                      style={{ minWidth: 280, maxWidth: 280, width: 280 }}
                    >
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-2 overflow-hidden border border-[#E0E6F0]">
                          <img
                            src={fixture?.hometeam_logo}
                            alt={fixture?.hometeam}
                            className="h-10 w-10 p-1 object-contain"
                          />
                        </div>
                        <div className="text-xs text-[#444] font-semibold uppercase tracking-wider text-center">
                          {fixture?.short?.find((s) => s.team_type === "home")
                            ?.code || ""}
                        </div>
                      </div>
                      <div className="flex flex-col items-center justify-center flex-shrink-0 min-w-[60px]">
                        {fixture?.status === "NS" ?
                          <>
                            <span className="text-sm font-semibold text-[#222] whitespace-nowrap text-center">
                              {formatMatchTime(fixture?.start)}
                            </span>
                          </> : fixture?.status === "FT" ?
                            <>
                              <span className="text-lg font-bold text-[#222] whitespace-nowrap mb-1">
                                {fixture?.hometeam_goal} - {fixture?.awayteam_goal}
                              </span>
                              <div>
                                {statusBadge(fixture?.status)}
                              </div>
                            </> : isCheckStatusLive(fixture?.status) ?
                              <>
                                <span className="text-lg font-bold text-[#222] whitespace-nowrap mb-1">
                                  {fixture?.hometeam_goal} - {fixture?.awayteam_goal}
                                </span>
                                <div>
                                  {statusBadge(fixture?.status)}
                                </div>
                              </>
                              : (
                                <span className="text-sm font-semibold text-[#222] whitespace-nowrap text-center">
                                  {formatMatchTime(fixture?.start)}
                                </span>
                              )}
                      </div>
                      <div className="flex flex-col items-center flex-1">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mb-2 p-1 overflow-hidden border border-[#E0E6F0]">
                          <img
                            src={fixture.awayteam_logo}
                            alt={fixture.awayteam}
                            className="h-10 w-10 object-contain"
                          />
                        </div>
                        <div className="text-xs text-[#444] text-center font-semibold uppercase tracking-wider">
                          {fixture.short?.find((s) => s?.team_type === "away")
                            ?.code || ""}
                        </div>
                      </div>
                    </div>
                    {index < day.fixtures.length - 1 && (
                      <div className="flex items-center justify-center px-3 flex-shrink-0">
                        <div className="w-px h-12 bg-gradient-to-b from-transparent via-[#E0E6F0] to-transparent"></div>
                      </div>
                    )}
                  </React.Fragment>
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="text-gray-400 text-center py-8">No fixtures found.</div>
        )}
      </div>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .mobile-header-bg { background: #0080FC; }
        
        /* Base styles for all devices */
        section { 
          border-radius: 1rem !important; 
        }
        .rounded-2xl { 
          border-radius: 1rem !important; 
        }
        .mobile-header-bg { 
          background: #3888f9; 
          border-radius: 1rem 1rem 0 0 !important; 
        }
        
        /* Mobile specific styles */
        @media (max-width: 640px) {
          section { 
            border-radius: 1.125rem !important; 
          }
          .rounded-2xl { 
            border-radius: 1.125rem !important; 
          }
          .mobile-header-bg { 
            background: #3888f9; 
            border-radius: 1.125rem 1.125rem 0 0 !important; 
          }
          .mb-10 { 
            margin-bottom: 2rem !important; 
          }
          .gap-4 { 
            gap: 1rem !important; 
          }
        }
        
        /* Tablet specific styles */
        @media (min-width: 641px) and (max-width: 1024px) {
          section { 
            border-radius: 1rem !important; 
          }
          .rounded-2xl { 
            border-radius: 1rem !important; 
          }
          .mobile-header-bg { 
            background: #0080FC; 
            border-radius: 1rem 1rem 0 0 !important; 
          }
        }
        
        /* Desktop styles */
        @media (min-width: 1025px) {
          section { 
            border-radius: 1rem !important; 
          }
          .rounded-2xl { 
            border-radius: 1rem !important; 
          }
          .mobile-header-bg { 
            background: #0080FC; 
            border-radius: 1rem 1rem 0 0 !important; 
          }
        }
      `}</style>
    </section>
  );
}